import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import {
  MenuItem,
  Select,
  OutlinedInput,
  SelectChangeEvent
} from '@mui/material';
import { TIMELINE_PERIOD } from './timelinePeriod';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 0,
    minWidth: theme.spacing(10.5)
  },
  item: {
    marginRight: theme.spacing(2)
  },
  select: {
    minHeight: theme.spacing(2.5),
    padding: theme.spacing(1, 3, 1, 1.5)
  }
}));

type TimelineMenuItemProps = {
  period: TIMELINE_PERIOD;
  onPeriodChange: (
    event: SelectChangeEvent<TIMELINE_PERIOD>,
    child: React.ReactNode
  ) => void;
};

export const TimelinePeriodMenu: React.FC<TimelineMenuItemProps> = ({
  period,
  onPeriodChange
}: TimelineMenuItemProps) => {
  const outlineInput = useMemo(() => <OutlinedInput />, []);

  const classes = useStyles();
  const selectClasses = useMemo(
    () => ({ root: classes.root, select: classes.select }),
    [classes.root, classes.select]
  );
  const intl = useIntl();
  const menuProps = useMemo(
    () => ({
      MenuListProps: {
        dense: true,
        'aria-label': intl.formatMessage({ id: 'calendar.periodDropdown' })
      },
      PaperProps: {
        role: 'main'
      }
    }),
    [intl]
  );

  return (
    <Select
      data-testid="TimelinePeriodDropdown"
      value={period}
      input={outlineInput}
      onChange={onPeriodChange}
      MenuProps={menuProps}
      classes={selectClasses}
      className={classes.item}
    >
      <MenuItem data-testid="DayMenuItem" value={TIMELINE_PERIOD.DAY}>
        <FormattedMessage id="calendar.day" />
      </MenuItem>
      <MenuItem data-testid="WeekMenuItem" value={TIMELINE_PERIOD.WEEK}>
        <FormattedMessage id="calendar.week" />
      </MenuItem>
    </Select>
  );
};

export default TimelinePeriodMenu;
